"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

interface AnimationContextType {
  bgAnimation: "none" | "scanning" | "phishing";
  bgOpacity: number;
  startScanning: () => void;
  startPhishing: () => void;
  stopAnimation: () => void;
}

const AnimationContext = createContext<AnimationContextType | undefined>(undefined);

export function AnimationProvider({ children }: { children: ReactNode }) {
  const [bgAnimation, setBgAnimation] = useState<"none" | "scanning" | "phishing">("none");
  const [bgOpacity, setBgOpacity] = useState(1);
  const timers = React.useRef<{ fade?: number | null; reset?: number | null }>({ fade: null, reset: null });

  const clearTimers = () => {
    if (timers.current.fade) {
      window.clearTimeout(timers.current.fade as number);
      timers.current.fade = null;
    }
    if (timers.current.reset) {
      window.clearTimeout(timers.current.reset as number);
      timers.current.reset = null;
    }
  };

  const startScanning = () => {
    clearTimers();
    setBgAnimation("scanning");
    setBgOpacity(1);
  };

  const startPhishing = () => {
    clearTimers();
    setBgAnimation("phishing");
    setBgOpacity(1);

    // Fade after 3s and reset after 5s (3s visible + 2s fade)
    timers.current.fade = window.setTimeout(() => setBgOpacity(0), 3000);
    timers.current.reset = window.setTimeout(() => {
      setBgAnimation("none");
      setBgOpacity(1);
      clearTimers();
    }, 5000);
  };

  const stopAnimation = () => {
    clearTimers();
    // quick fade and reset
    setBgOpacity(0);
    timers.current.reset = window.setTimeout(() => {
      setBgAnimation("none");
      setBgOpacity(1);
      clearTimers();
    }, 1500);
  };

  React.useEffect(() => {
    return () => clearTimers();
  }, []);

  return (
    <AnimationContext.Provider value={{ bgAnimation, bgOpacity, startScanning, startPhishing, stopAnimation }}>
      {children}
    </AnimationContext.Provider>
  );
}

export function useAnimation() {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error("useAnimation must be used within AnimationProvider");
  }
  return context;
}
